import { useCallback, useState } from 'react';
import { Button, Text } from 'rizzui';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Loader } from '../../../components/loader/SpinnerLogo';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';

const columnOrder = [
    'commercialNetworkName',
    'contractCode',
    'courseLabel',
    'studentFullName',
    'documentNumber',
    'email',
    'typeLabel',
];

const FacilitatorPendingExtras = () => {
    const { handleErrors } = useHandleErrors();
    const service = new FacilitatorCallsService();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [], 1, 25);

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listPendingContractExtras(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const rows = response?.data ?? [];
    const lastPage = response?.lastPage ?? 1;

    const orderedData = () => rows.map((row: any) => ({
        id: row.id,
        contractId: row.contract?.id,
        commercialNetworkName: row.contract?.commercialNetwork?.name || 'N/A',
        contractCode: row.contract?.code || 'Contrato',
        courseLabel: row.product ? `${row.product.code ? `${row.product.code} - ` : ''}${row.product.name}` : row.title || 'N/A',
        studentFullName: [row.name, row.firstName, row.lastName].filter(Boolean).join(' ') || 'N/A',
        documentNumber: row.documentNumber || 'N/A',
        email: row.email || 'N/A',
        typeLabel: row.type === 'online_course' ? 'Curso online extra' : 'Curso extra',
    }));

    const handleMarkDone = async (extraId: string) => {
        try {
            const apiResponse = await (await service.markContractExtraDone(extraId)).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success('Extra marcado como hecho');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (!loading && error) {
        return (
            <Page container="fluid">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No se ha podido cargar el listado de extras pendientes.
                </div>
            </Page>
        );
    }

    return (
        <Page container="fluid">
            <CustomTable
                id="facilitator-pending-extras-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[
                    'contractId'
                ]}
                overrideColumns={[
                    {
                        key: 'contractCode',
                        render: (row: any) => (
                            <Link className="font-bold text-primary" to={`${menuRoutes.contracts.path}/${row.row.original.contractId}/profile${menuRoutes.contracts.profile.extras}`}>
                                {row.row.original.contractCode}
                            </Link>
                        ),
                    },
                ]}
                actions={[
                    {
                        label: 'Done',
                        render: (row: any) => (
                            <Button size="sm" onClick={() => handleMarkDone(row.id)}>
                                Hecho
                            </Button>
                        ),
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                filter={false}
            >
                <Button variant="outline" onClick={() => refetch()}>
                    Actualizar
                </Button>
            </CustomTable>
        </Page>
    );
};

export default FacilitatorPendingExtras;
